/**
 * Compensation logic for the chat-service `updateChat` customerNumber contract.
 *
 * Backend receipt: `updateChat` drops a falsy `customerNumber` from the write
 * (`const inputToUpdate = customerNumber ? chat : rest` — a deliberate GSI
 * NULL-key guard), so submitting an empty customerNumber is a silent
 * server-side no-op: the OLD value survives on the chat. If the Settings form
 * committed the cleared field as clean it would show empty while the server
 * still holds the old number, and the divergence surfaces on the next reload.
 *
 * This resolver detects that case so the caller can, instead of pretending the
 * clear stuck, warn the operator and re-baseline the field to the value the
 * server actually kept.
 */

export interface CustomerNumberClearResolution {
  /**
   * True when the operator submitted an empty/whitespace customerNumber for a
   * chat that already HAD one — a clear the backend will silently ignore.
   */
  isBlockedClear: boolean;
  /**
   * The value the form should write AND re-baseline to. Server truth when a
   * clear is blocked (so the field re-seeds to what the server kept, not to the
   * submitted empty string); otherwise the submitted value unchanged.
   */
  effectiveCustomerNumber: string;
}

export const resolveCustomerNumberClear = (
  submittedCustomerNumber: string,
  serverCustomerNumber: string | null | undefined
): CustomerNumberClearResolution => {
  const server = serverCustomerNumber ?? '';
  const isBlockedClear =
    submittedCustomerNumber.trim() === '' && server.trim() !== '';

  return {
    isBlockedClear,
    effectiveCustomerNumber: isBlockedClear ? server : submittedCustomerNumber,
  };
};
